// RichTextEditor.js
import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import "./RichTextEditor.css";
import { useTheme } from '../contexts/ThemeContext';
import { Bold, Italic, Code, List, Link, Image, Type } from 'lucide-react';
// Add this import near the other imports
import { useEditor } from '../contexts/EditorContext';

const Font = ReactQuill.Quill.import('formats/font');
Font.whitelist = ['monospace', 'serif', 'sans-serif', 'fira-sans', 'playfair', 'roboto', 'oswald', 'inconsolata'];
ReactQuill.Quill.register(Font, true);

console.log('ReactQuill Version:', ReactQuill.version);
console.log('Quill Core Version:', ReactQuill.Quill.version);

const RichTextEditor = ({ 
  initialValue = '', 
  onChange, 
  placeholder = "Press '/' for commands...", 
  categories, 
  selectedCategoryId, 
  onCategoryChange 
}) => {
  const [editorKey, setEditorKey] = useState(0);
  const [value, setValue] = useState('');
  const quillRef = useRef(null);
  const editorId = useRef(`editor-${Math.random().toString(36).substr(2, 9)}`).current;
  const theme = useTheme();
  const previousValueRef = useRef('');
  const { setEditor } = useEditor();
  
  // Instead of a single flag, track code sections
  const hasCodeSectionsRef = useRef(false);
  const codeSectionsRef = useRef([]);

  useEffect(() => {
    if (!quillRef.current) return;
  
    const editor = quillRef.current.getEditor();
    const currentContents = editor.root.innerHTML;
  
    // Prevent unnecessary updates if content is the same
    if (currentContents === initialValue) {
      return;
    }
  
    // Preserve cursor position before updating
    const range = editor.getSelection();
  
    // Parse the new note content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = initialValue;
  
    // Find all code blocks
    const codeBlocks = tempDiv.querySelectorAll('pre.ql-syntax');
    const codeSections = Array.from(codeBlocks).map(block => ({
      content: block.outerHTML,
      start: tempDiv.innerHTML.indexOf(block.outerHTML),
      end: tempDiv.innerHTML.indexOf(block.outerHTML) + block.outerHTML.length,
    }));
  
    // Store code sections reference
    codeSectionsRef.current = codeSections;
    hasCodeSectionsRef.current = codeSections.length > 0;
  
    // **🔹 Fix: Ensure Quill Resets Correctly**
    if (hasCodeSectionsRef.current) {
      // If switching to a code note, clear existing formatting
      editor.removeFormat(0, editor.getLength());
    } else {
      // **Force Quill to reset styles when switching FROM a code note**
      editor.format('code-block', false);
    }
  
    // **Safely paste new content**
    editor.clipboard.dangerouslyPasteHTML(initialValue);
  
    // Restore cursor position
    if (range) {
      editor.setSelection(range.index, range.length);
    }
  
    // **Fix: Ensure code formatting is only applied if necessary**
    if (hasCodeSectionsRef.current) {
      setTimeout(() => {
        const editorContent = editor.root.innerHTML;
        codeSections.forEach(section => {
          const start = editorContent.indexOf(section.content);
          if (start !== -1) {
            editor.formatText(start, section.content.length, 'code-block', true);
          }
        });
      }, 0);
    }
  }, [initialValue]);

// REPLACE the broken useEffect with this corrected version:

useEffect(() => {
  if (!quillRef.current) return;
  
  const editor = quillRef.current.getEditor();
  if (editor) {
    setEditor(editor);
    
    // NEW: Add drop handlers to the editor's DOM element
    const editorElement = editor.root;
    
    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'copy';
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      const droppedText = e.dataTransfer.getData('text/plain');
      if (!droppedText.trim()) return;

      // Get current cursor position or end of document
      const selection = editor.getSelection();
      const insertIndex = selection ? selection.index : editor.getLength();

      // Insert the dropped text at cursor position
      editor.insertText(insertIndex, droppedText);
      
      // Move cursor to end of inserted text
      editor.setSelection(insertIndex + droppedText.length);
    };
    
    // Attach listeners to the editor's DOM element
    editorElement.addEventListener('dragover', handleDragOver);
    editorElement.addEventListener('drop', handleDrop);
    
    // Return cleanup function for event listeners
    return () => {
      editorElement.removeEventListener('dragover', handleDragOver);
      editorElement.removeEventListener('drop', handleDrop);
      setEditor(null);
    };
  }
  
  
  // Cleanup function to clear the editor reference when component unmounts
  return () => {
    setEditor(null);
  };
}, [quillRef, setEditor]);
  

  const handleChange = (content, delta, source, editor) => {
    if (content === previousValueRef.current) {
      return;
    }

    // Preserve code sections if they exist
    if (hasCodeSectionsRef.current) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      
      // Ensure code blocks have proper classes
      const codeBlocks = tempDiv.querySelectorAll('pre');
      codeBlocks.forEach(block => {
        if (!block.classList.contains('ql-syntax')) {
          block.classList.add('ql-syntax');
          block.setAttribute('spellcheck', 'false');
        }
      });
      
      content = tempDiv.innerHTML;
    }

    setValue(content);
    previousValueRef.current = content;

    if (source === 'user' && onChange) {
      onChange(content);
    }
  };

  const modules = {
    toolbar: false,
    clipboard: {
      matchVisual: false
    },
    history: {
      delay: 2000,
      maxStack: 500,
      userOnly: true
    }
  };

  const ToolbarButton = ({ onClick, children, active }) => (
    <button
      onClick={onClick}
      className={`format-button ${active ? 'active' : ''}`}
    >
      {children}
    </button>
  );

  const handleFormat = (format) => {
    const editor = quillRef.current.getEditor();
    
    // If formatting a code block, handle it specially
    if (format === 'code-block') {
      const range = editor.getSelection();
      if (range) {
        const currentFormat = editor.getFormat(range);
        editor.format('code-block', !currentFormat['code-block']);
      }
    } else {
      editor.format(format, !editor.getFormat()[format]);
    }
  };

  return (
    <div className={`notion-style-editor ${editorId}`}>
      

      <ReactQuill
  ref={quillRef}
  theme="snow"
  defaultValue={value}
  onChange={handleChange}
  modules={modules}
  placeholder={placeholder}
  className="notion-editor"
/>
    </div>
  );
};

export default React.memo(RichTextEditor);